import { claudeAgentApi } from "@/lib/api/claude-agent";
import { useSlideStore } from "@/stores/slide-store";
import type { TransitionType } from "@/stores/slide-store";

interface FetchSlideManifestOptions {
  devContainerId: string;
  getIdToken: () => Promise<string | null>;
}

/**
 * Fetch slides.json from the dev container and sync to Zustand store.
 * Standalone function (not a hook) — callable from event handlers.
 *
 * Returns true if this is a slides project, false otherwise.
 */
export async function fetchSlideManifest({
  devContainerId,
  getIdToken,
}: FetchSlideManifestOptions): Promise<boolean> {
  try {
    const token = await getIdToken();
    if (!token) return false;

    const { content } = await claudeAgentApi.readFile(
      token,
      devContainerId,
      "/workspaces/src/slides/slides.json",
    );

    const manifest = JSON.parse(content);
    const slides = manifest?.slides;

    if (!Array.isArray(slides) || slides.length === 0) {
      useSlideStore.getState().setIsSlideProject(false);
      return false;
    }

    // Sort by position field if present (set by drag-and-drop reorder)
    const sorted = [...slides].sort((a, b) => {
      if (a.position !== undefined && b.position !== undefined) {
        return a.position - b.position;
      }
      return 0;
    });

    const store = useSlideStore.getState();
    store.setIsSlideProject(true);
    store.setSlides(sorted);

    // Sync presentation config from slides.json
    if (manifest.config) {
      const { transition, aspectRatio, ...presentationFields } =
        manifest.config;
      store.setPresentationConfig({
        ...(transition ? { transition: transition as TransitionType } : {}),
        ...presentationFields,
      });
    }

    return true;
  } catch (error: unknown) {
    // Only mark as non-slides project on 404 (file doesn't exist) or JSON parse error
    // Transient errors (401, 500, network) should not flip the flag
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (status === 404 || error instanceof SyntaxError) {
      useSlideStore.getState().setIsSlideProject(false);
    }
    return false;
  }
}
